// ViewFacultyTimetable.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { baseApiURL } from '../../baseUrl';
import toast from 'react-hot-toast';

const ViewFacultyTimetable = ({ facultyId }) => {
  const [timetable, setTimetable] = useState({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
  });
  const [loading, setLoading] = useState(true);
  const [facultyInfo, setFacultyInfo] = useState({
    name: '',
    department: '',
    employeeId: ''
  });


  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    if (facultyId) {
      loadFacultyTimetable();
    }
  }, [facultyId]);

  const loadFacultyTimetable = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${baseApiURL()}/faculty/details/getDetails`, { 
        employeeId: facultyId 
      });

      
      if (response.data.success && response.data.user[0]) {
        const faculty = response.data.user[0];
        setFacultyInfo({
          name: `${faculty.firstName} ${faculty.middleName ? faculty.middleName + " " : ""}${faculty.lastName}`,
          department: faculty.department,
          employeeId: faculty.employeeId
      });

        // Initialize empty timetable structure
        const newTimetable = {
          Monday: [],
          Tuesday: [],
          Wednesday: [],
          Thursday: [],
          Friday: [],
          Saturday: [],
        };

        // Populate with data if exists
        if (faculty.timetable && Array.isArray(faculty.timetable)) {
          faculty.timetable.forEach(dayData => {
            if (dayData.day && dayData.periods) {
              newTimetable[dayData.day] = dayData.periods;
            }
          });
        }

        setTimetable(newTimetable);
      } else {
        toast.error("No timetable data found for this faculty");
      }
    } catch (error) {
      console.error("Error loading faculty timetable:", error);
      toast.error("Failed to load faculty timetable");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format time (e.g., "09:00" -> "9:00 AM")
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const hourNum = parseInt(hours, 10);
      const period = hourNum >= 12 ? 'PM' : 'AM';
      const displayHour = hourNum % 12 || 12;
      
      return `${displayHour}:${minutes} ${period}`;
    } catch (e) {
      return timeString; // Return original if formatting fails
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs overflow-hidden border border-slate-200 dark:border-slate-800">
      {/* Faculty Info Header */}
      <div className="bg-slate-50 dark:bg-slate-950 px-6 py-4 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">
          {facultyInfo.name || 'Faculty Timetable'}
        </h2>
        <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-slate-600 dark:text-slate-400">
          <div>
            <span className="font-bold">Employee ID:</span> <span className="font-mono text-indigo-600 dark:text-indigo-400">{facultyInfo.employeeId || 'N/A'}</span>
          </div>
          <div>
            <span className="font-bold">Department:</span> {facultyInfo.department || 'N/A'}
          </div>
        </div>
      </div>

      {/* Timetable Grid */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
          <thead className="bg-slate-50 dark:bg-slate-950">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                Day
              </th>
              {Array.from({ 
                length: Math.max(...daysOfWeek.map(day => timetable[day].length), 1) 
              }).map((_, i) => (
                <th 
                  key={i} 
                  className="px-6 py-3 text-center text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider"
                >
                  Period {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            {daysOfWeek.map((day) => (
              <tr key={day} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-slate-900 dark:text-white text-center">
                  {day}
                </td>
                {timetable[day].map((period, index) => (
                  <td key={index} className="px-6 py-4 whitespace-nowrap">
                    {period.subject ? (
                      <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
                        <div className="font-bold text-xs text-indigo-600 dark:text-indigo-400">
                          {period.subject}
                        </div>
                        <div className="text-[11px] text-slate-700 dark:text-slate-300 font-semibold mt-1">
                          {period.branch} - Sem {period.semester} Sec {period.section}
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium mt-1">
                          {formatTime(period.startTime)} - {formatTime(period.endTime)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-slate-400 dark:text-slate-600 text-xs italic">No class scheduled</div>
                    )}
                  </td>
                ))}
                {/* Fill empty cells if no periods */}
                {Array.from({ 
                  length: Math.max(0, Math.max(...daysOfWeek.map(d => timetable[d].length)) - timetable[day].length) 
                }).map((_, i) => (
                  <td key={`empty-${i}`} className="px-6 py-4 text-center">
                    <div className="text-slate-400 dark:text-slate-600 text-xs italic">-</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {Object.values(timetable).every(day => day.length === 0) && (
        <div className="p-8 text-center text-slate-500 dark:text-slate-400">
          <h3 className="mt-2 text-sm font-bold text-slate-900 dark:text-white">No timetable found</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            This faculty member doesn't have any scheduled classes yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default ViewFacultyTimetable;